import { ReactNode, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Sidebar from "./Sidebar";
import Footer from "./Footer";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface SidebarLayoutProps {
  children: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  showBreadcrumb?: boolean;
  showContactButton?: boolean;
  showTopBar?: boolean;
}

export default function SidebarLayout({
  children,
  breadcrumbs,
  showBreadcrumb = true,
  showContactButton = true,
  showTopBar = true,
}: SidebarLayoutProps) {
  const [showFixedBtn, setShowFixedBtn] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const topBarRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = topBarRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFixedBtn(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "-1px 0px 0px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setFooterVisible(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "0px 0px -50% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const buttonClasses =
    "inline-flex items-center gap-2 px-5 py-2.5 bg-[#025ab0] text-white text-sm font-medium rounded-full hover:bg-[#024a90] transition-colors whitespace-nowrap";

  return (
    <div className="min-h-screen bg-[#f0efef]">
      <Sidebar footerVisible={footerVisible} />
      <div className="lg:pl-[240px] pt-14 lg:pt-0">
        {/* Top bar */}
        {showTopBar && (
          <div
            ref={topBarRef}
            className="flex items-center justify-between px-4 md:px-6 lg:px-10 h-28"
          >
            {/* Breadcrumb */}
            {showBreadcrumb && (
              <nav className="flex items-center gap-2 text-sm text-gray-500">
                <Link
                  to="/"
                  className="hover:text-gray-700 transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-line" />
                  </div>
                </Link>
                {breadcrumbs &&
                  breadcrumbs.map((crumb, i) => (
                    <span key={i} className="flex items-center gap-2">
                      <span>/</span>
                      {crumb.path ? (
                        <Link
                          to={crumb.path}
                          className="text-gray-700 hover:text-black transition-colors"
                        >
                          {crumb.label}
                        </Link>
                      ) : (
                        <span className="text-gray-700">{crumb.label}</span>
                      )}
                    </span>
                  ))}
              </nav>
            )}

            {/* Contact button inside top bar */}
            {showContactButton && (
              <Link to="/contact" className={buttonClasses}>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line text-sm" />
                </div>
                お問い合わせ
              </Link>
            )}
          </div>
        )}

        {/* Fixed contact button - shows only when top bar scrolls away */}
        {showContactButton && showFixedBtn && (
          <div className="fixed top-14 lg:top-0 right-4 md:right-6 lg:right-10 z-30 py-2.5">
            <Link to="/contact" className={buttonClasses}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-line text-sm" />
              </div>
              お問い合わせ
            </Link>
          </div>
        )}

        <main>{children}</main>
        <div ref={footerRef}>
          <Footer />
        </div>
      </div>
    </div>
  );
}